import { c as _c } from "react/compiler-runtime";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { findMarkupPanorama, loadMarkupPanorama } from "@pages/project-details/project-markups/sidepanel/preview/panorama/fetch-markup-panorama";
import { findMarkupPointCloud, loadMarkupPointCloud } from "@pages/project-details/project-markups/sidepanel/preview/point-cloud/fetch-markup-point-cloud";
import { useAppDispatch } from "@store/store-helper";
import { sentryCaptureError } from "@utils/sentry-utils";
import { useEffect, useState } from "react";

/** What the annotation can be previewed in */

/** Preview of the annotation, in the order the states go through */

/** Preview states that do not change anymore */

/** A settled preview kept for later, until the signed URLs it holds could expire */

/** How long a found preview is kept, well within the validity of the signed URLs it holds */
const KEPT_PREVIEW_LIFETIME_MS = 600_000;

/** Longest the lookup of a preview holds back the side panel */
const MAX_FINDING_DURATION_MS = 3000;
/**
 * Finds and loads what the selected annotation can be previewed in: the panorama it was placed
 * on, or else the point cloud around it. Found previews are kept, so showing an annotation again
 * or the next one, looked up ahead, is immediate.
 */
export function useMarkupPreview(t0) {
  const $ = _c(32);
  const {
    markupId,
    isMarkupLoaded,
    isEnabled,
    nextMarkupId
  } = t0;
  const dispatch = useAppDispatch();
  const {
    projectId
  } = useMarkupContext();
  let t1;
  if ($[0] !== projectId) {
    t1 = {
      projectId
    };
    $[0] = projectId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const projectApiClient = useProjectApiClient(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      status: "finding"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [state, setState] = useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [settledPreviews, setSettledPreviews] = useState(t3);
  let t4;
  if ($[4] !== markupId || $[5] !== settledPreviews) {
    t4 = markupId ? settledPreviews.get(markupId) : undefined;
    $[4] = markupId;
    $[5] = settledPreviews;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const settled = t4;
  const isSettled = !!settled;
  let t5;
  if ($[7] !== nextMarkupId || $[8] !== settledPreviews) {
    t5 = !!nextMarkupId && settledPreviews.has(nextMarkupId);
    $[7] = nextMarkupId;
    $[8] = settledPreviews;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const isNextSettled = t5;
  let t6;
  let t7;
  if ($[10] !== markupId || $[11] !== settled) {
    t6 = () => {
      if (markupId && settled && Date.now() > settled.expiresAt) {
        setSettledPreviews(previous => {
          const next = new Map(previous);
          next.delete(markupId);
          return next;
        });
      }
    };
    t7 = [markupId, settled];
    $[10] = markupId;
    $[11] = settled;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[14] !== dispatch || $[15] !== isEnabled || $[16] !== isMarkupLoaded || $[17] !== isSettled || $[18] !== markupId || $[19] !== projectApiClient) {
    t8 = () => {
      if (!isEnabled || !markupId || !isMarkupLoaded || isSettled) {
        return;
      }
      const controller = new AbortController();
      const update = function update(next_0) {
        if (!controller.signal.aborted) {
          setState({
            ...next_0,
            markupId
          });
        }
      };
      const findingTimeout = setTimeout(() => update({
        status: "loading"
      }), MAX_FINDING_DURATION_MS);
      loadPreview({
        dispatch,
        markupId,
        projectApiClient,
        signal: controller.signal,
        onFound: placeId => {
          clearTimeout(findingTimeout);
          update({
            status: "loading",
            placeId
          });
        }
      }).then(content => {
        if (!controller.signal.aborted) {
          settlePreview(setSettledPreviews, markupId, content);
        }
      }).catch(error => {
        if (controller.signal.aborted) {
          return;
        }
        sentryCaptureError({
          error,
          title: "Failed to load annotation preview"
        });
        update({
          status: "unavailable"
        });
      }).finally(() => clearTimeout(findingTimeout));
      return () => {
        clearTimeout(findingTimeout);
        controller.abort();
      };
    };
    t9 = [dispatch, isEnabled, isMarkupLoaded, isSettled, markupId, projectApiClient];
    $[14] = dispatch;
    $[15] = isEnabled;
    $[16] = isMarkupLoaded;
    $[17] = isSettled;
    $[18] = markupId;
    $[19] = projectApiClient;
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[22] !== dispatch || $[23] !== isEnabled || $[24] !== isNextSettled || $[25] !== isSettled || $[26] !== nextMarkupId || $[27] !== projectApiClient) {
    t10 = () => {
      if (!isEnabled || !nextMarkupId || isNextSettled || !isSettled) {
        return;
      }
      const controller_0 = new AbortController();
      loadPreview({
        dispatch,
        markupId: nextMarkupId,
        projectApiClient,
        signal: controller_0.signal,
        onFound: _temp
      }).then(content_0 => {
        if (!controller_0.signal.aborted) {
          settlePreview(setSettledPreviews, nextMarkupId, content_0);
        }
      }).catch(_temp2);
      return () => controller_0.abort();
    };
    t11 = [dispatch, isEnabled, isNextSettled, isSettled, nextMarkupId, projectApiClient];
    $[22] = dispatch;
    $[23] = isEnabled;
    $[24] = isNextSettled;
    $[25] = isSettled;
    $[26] = nextMarkupId;
    $[27] = projectApiClient;
    $[28] = t10;
    $[29] = t11;
  } else {
    t10 = $[28];
    t11 = $[29];
  }
  useEffect(t10, t11);
  if (!isEnabled) {
    let t12;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        status: "unavailable"
      };
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    return t12;
  }
  if (settled) {
    return settled;
  }
  if (!isMarkupLoaded || state.markupId !== markupId) {
    let t12;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        status: "finding"
      };
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    return t12;
  }
  return state;
}

/** @returns the panorama or point cloud the annotation is previewed in, once found */
function _temp2() {}
function _temp() {}
export function getMarkupPreviewPlaceId(previewState) {
  if (previewState.status === "loading") {
    return previewState.placeId;
  }
  if (previewState.status === "ready") {
    const {
      content
    } = previewState;
    return content.kind === "panorama" ? content.panorama.id : content.pointCloud.id;
  }
}

/** Keeps the preview of an annotation, to show it at once the next time */
function settlePreview(setSettledPreviews, markupId, content) {
  const expiresAt = Date.now() + KEPT_PREVIEW_LIFETIME_MS;
  const preview = content ? {
    status: "ready",
    content,
    expiresAt
  } : {
    status: "unavailable",
    expiresAt
  };
  setSettledPreviews(previous => new Map(previous).set(markupId, preview));
}
/** @returns the panorama of the annotation, or else the point cloud around it, if any */
async function loadPreview({
  dispatch,
  markupId,
  projectApiClient,
  signal,
  onFound
}) {
  const props = {
    markupId,
    projectApiClient,
    signal
  };
  const panorama = await dispatch(findMarkupPanorama(props));
  if (panorama) {
    onFound(panorama.id);
    const loaded = await dispatch(loadMarkupPanorama({
      ...props,
      panorama
    }));
    return loaded && {
      kind: "panorama",
      panorama: loaded
    };
  }
  const pointCloud = await dispatch(findMarkupPointCloud(props));
  if (pointCloud) {
    onFound(pointCloud.pointCloud.id);
    const loaded = await dispatch(loadMarkupPointCloud({
      ...props,
      ...pointCloud
    }));
    return loaded && {
      kind: "pointCloud",
      pointCloud: loaded
    };
  }
}